import { create } from "zustand";
import { persist } from "zustand/middleware";

interface User {
  username: string;
  isAuthenticated: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null });

        // Simulate API call delay
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Check credentials
        const isValidCredentials =
          username === "ceo" && password === "alonballon";

        if (isValidCredentials) {
          const user = { username, isAuthenticated: true };
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          return true;
        } else {
          set({
            isLoading: false,
            error: "Invalid username or password",
          });
          return false;
        }
      },

      logout: () => {
        // Clear the user's display name from localStorage
        localStorage.removeItem("herocon_user_name");
        localStorage.removeItem("herocon_visited");

        // Reset viewer state to prevent auto-loading on next login
        import("./viewerStore").then(({ useViewerStore }) => {
          useViewerStore.getState().reset();
        });

        // Clear validation results so Output tab buttons are disabled on next login
        import("./rulesStore").then(({ useRulesStore }) => {
          const { clearValidationResults } = useRulesStore.getState();
          clearValidationResults();
        });

        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: "herocon-auth",
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
